import { useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useRoles from '@/hooks/useRoles';
import staffMemberService, { RemoveDirectReportProps } from '@/services/staffMember';
import Checkbox from '@/trio-ui/Checkbox';
import DatePicker from '@/trio-ui/DatePicker';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import UserInitials from '@/trio-ui/UserInitials';
import { FetchApiIds, ValuesWithLabel } from '@/types';
import { Team, TeamArea } from '@/types/api';
import { JobInfo, Seniority, StaffMember } from '@/types/api/staffMember';
import { MONTHS } from '@/utils/dates';
import { seniorityOptions, teamAreaOptions } from '@/utils/dropdownOptions';
import { requiredMessage } from '@/utils/formsValidation';
import AssignedUsers from '../AssignedUsers';
import MenuHoriz from '../Menu';

export const jobFormSchema = yup.object({
  title: yup.string().required(requiredMessage),
  seniority: yup.mixed<Seniority>().oneOf(seniorityOptions).required(requiredMessage),
  area: yup.mixed<TeamArea>().oneOf(teamAreaOptions).required(requiredMessage),
  teamId: yup.string().required(requiredMessage),
  fromDate: yup.date().required(requiredMessage),
  toDate: yup.date().when('isCurrentJob', {
    is: false,
    then: yup.date().required(requiredMessage),
  }),
  isCurrentJob: yup.boolean(),
});

export type FormData = {
  title: string;
  seniority: Seniority;
  area: TeamArea;
  teamId: string;
  directLeaderId: string;
  directReportsIds?: string[];
  fromDate: string;
  toDate?: string;
};

export type Props = {
  isEditing?: boolean;
  onEditClick?: () => void;
  onEditCancel?: () => void;
  onDeleteJob?: (jobId: string) => void;
  teamsList?: Team[];
  leaderSearch?: string;
  onLeaderSearch?: (value: string) => void;
  leadersFound?: StaffMember[];
  reportersSearch?: string;
  onReportersSearch?: (value: string) => void;
  reportersFound?: StaffMember[];
  onSubmit?: (data: FormData) => void;
  jobInfo?: JobInfo;
  onAddCancel?: () => void;
  isLoading?: boolean;
  onRemoveReporterClick?: (
    data: Omit<RemoveDirectReportProps, 'staffMemberId'> & { userId: string }
  ) => void;
  isRemovingReporter?: string;
  onAddReporterClick?: (data: { staffMemberId: string; jobInfoId: string }) => void;
  id?: string;
};
const today = new Date();

const DirectLeaderRender = ({ staffMember }: { staffMember?: StaffMember }): JSX.Element | null => {
  if (!staffMember) {
    return null;
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-pl-6">
      <UserInitials
        className="!tw-w-6 !tw-h-6 !tw-mr-0"
        firstName={staffMember.person.firstName}
        lastName={staffMember.person.lastName}
        pictureUrl={staffMember.person.pictureUrl || undefined}
      />
      <p className="tw-text-primary-60 tw-leading-normal tw-text-sm tw-font-semibold">
        {staffMember.person.firstName} {staffMember.person.lastName}
      </p>
    </div>
  );
};

const StaffMemberJobItem = ({
  isEditing,
  onEditClick,
  onEditCancel,
  onDeleteJob,
  teamsList = [],
  leaderSearch,
  onLeaderSearch,
  leadersFound = [],
  reportersSearch,
  onReportersSearch,
  reportersFound = [],
  onSubmit,
  jobInfo,
  onAddCancel,
  isLoading = false,
  onRemoveReporterClick,
  isRemovingReporter,
  onAddReporterClick,
  id,
}: Props): JSX.Element => {
  const [leader, setLeader] = useState<StaffMember | undefined>();
  const [reporters, setReporters] = useState<StaffMember[]>([]);
  const toast = useToastContext();
  const { roleNames } = useRoles();

  const { handleSubmit, values, handleChange, errors, touched, resetForm, setFieldValue } =
    useFormik<yup.TypeOf<typeof jobFormSchema>>({
      initialValues: {
        title: '',
        seniority: undefined,
        area: undefined,
        teamId: undefined,
        fromDate: today,
        toDate: today,
        isCurrentJob: false,
      },
      validateOnBlur: false,
      validationSchema: jobFormSchema,
      onSubmit: (data) => {
        if (isLoading) return;

        if (!leader) {
          toast.showErrorFeedback('Please select a leader');
        } else if (data.area && data.title && data.fromDate && data.seniority && data.teamId) {
          const formData: FormData = {
            title: data.title,
            seniority: data.seniority,
            area: data.area,
            teamId: data.teamId,
            directLeaderId: leader.id,
            directReportsIds: jobInfo ? undefined : reporters.map((reporter) => reporter.id),
            fromDate: format(data.fromDate, 'yyyy-MM-dd'),
            toDate: data.toDate ? format(data.toDate, 'yyyy-MM-dd') : undefined,
          };

          onSubmit?.(formData);
        }
      },
    });

  const { data: jobInfoLeader } = useApiFetch(
    [FetchApiIds.GET_STAFF_MEMBER, jobInfo?.directLeaderId],
    async () => staffMemberService.getStaffMember({ staffMemberId: jobInfo?.directLeaderId || '' }),
    { enabled: Boolean(jobInfo?.directLeaderId) }
  );

  useEffect(() => {
    if (jobInfo && isEditing) {
      resetForm();
      setFieldValue('title', jobInfo.title);
      setFieldValue('seniority', jobInfo.seniority);
      setFieldValue('area', jobInfo.area);
      setFieldValue('teamId', jobInfo.teamId);
      setFieldValue('isCurrentJob', !jobInfo.toDate);
      setFieldValue('fromDate', new Date(jobInfo.fromDate));
      setFieldValue('toDate', jobInfo.toDate ? new Date(jobInfo.toDate) : undefined);

      setReporters(jobInfo.directReports.map((reporter) => reporter.staffMember).filter(Boolean));

      if (jobInfoLeader) {
        setLeader(jobInfoLeader);
      }
    }
  }, [jobInfo, isEditing, setFieldValue, resetForm, jobInfoLeader]);

  const handleSelectLeader = (personId: string): void => {
    const selectedUser = leadersFound.find((staffMember) => staffMember.person.id === personId);

    if (selectedUser) {
      setLeader(selectedUser);
    }
  };

  const handleSelectReporter = (personId: string): void => {
    const selectedUser = reportersFound.find((staffMember) => staffMember.person.id === personId);

    if (selectedUser) {
      if (jobInfo) {
        onAddReporterClick?.({
          jobInfoId: jobInfo.id,
          staffMemberId: selectedUser.id,
        });
      } else {
        setReporters((_reporters) => [..._reporters, selectedUser]);
      }
    } else {
      toast.showErrorFeedback('Staff member not found.');
    }
  };

  const handleRemoveReporter = (userId: string): void => {
    if (jobInfo) {
      const reporter = jobInfo.directReports.find((r) => r.staffMember?.person?.id === userId);

      if (reporter) {
        onRemoveReporterClick?.({
          jobInfoId: jobInfo.id,
          directReportId: reporter.id,
          userId,
        });
      } else {
        toast.showErrorFeedback('Direct reporter not found');
      }
    } else {
      setReporters((_reporters) => _reporters.filter((reporter) => reporter.id !== userId));
    }
  };

  const jobInfoTeam = teamsList.find((team) => team.id === jobInfo?.teamId);

  const formatDate = (date: string): string => {
    const [year, month] = date.split('-');

    return `${MONTHS[parseInt(month, 10) - 1]}, ${year}`;
  };

  return (
    <div
      className="tw-border tw-border-40 tw-rounded-2xl tw-shadow-elevation-centered-1 tw-p-6"
      id={id}
    >
      <div className="tw-flex tw-mb-6">
        <p className="tw-grow text-base tw-font-semibold tw-leading-5 tw-items-center">
          {jobInfo ? `${jobInfo?.seniority} ${jobInfo?.title}` : 'New Job'}
        </p>
        {jobInfo && !isEditing && (
          <MenuHoriz
            icon={MoreHorizIcon}
            menuId="profile-info-preview-job-menu"
            menuItems={[
              {
                label: 'Edit',
                menuItemIcon: CreateIcon,
                callback: (event) => onEditClick?.(),
              },
              {
                label: 'Delete',
                menuItemOptions: { warning: true },
                menuItemIcon: DeleteIcon,
                callback: (event) => {
                  event.stopPropagation();
                  onDeleteJob?.(jobInfo.id);
                },
              },
            ]}
          />
        )}
      </div>
      {jobInfo && !isEditing ? (
        <div className="tw-flex">
          <div className="tw-w-1/2 tw-border-r tw-border-neutral-40 tw-pr-5">
            <div className="tw-mb-8 tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center">
                <HomeWorkOutlinedIcon className="tw-mr-2 !tw-w-5 !tw-h-5 tw-fill-neutral-100" />
                <p className="tw-text-neutral-100 tw-text-base tw-leading-5">{jobInfo.area}</p>
              </div>
              <div className="tw-flex tw-items-center">
                <WorkspacesOutlinedIcon className="tw-mr-2 !tw-w-5 !tw-h-5 tw-fill-neutral-100" />
                <p className="tw-text-neutral-100 tw-text-base tw-leading-5">{jobInfoTeam?.name}</p>
              </div>
            </div>
            <div className="tw-block tw-text-sm tw-leading-normal">
              <span className="tw-text-neutral-80">
                {!jobInfo.toDate && 'Since '}
                <span className="tw-text-neutral-100">{formatDate(jobInfo.fromDate)}</span>
                {jobInfo.toDate ? (
                  <>
                    {' '}
                    to <span className="tw-text-neutral-100">{formatDate(jobInfo.toDate)}</span>
                  </>
                ) : (
                  <span className="tw-text-neutral-100 tw-font-semibold tw-ml-2">Current job</span>
                )}
              </span>
            </div>
          </div>
          <div className="tw-grow tw-pl-5">
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-8">
              <div className="tw-flex tw-items-center">
                <PersonPinOutlinedIcon className="tw-mr-2 !tw-w-5 !tw-h-5 tw-fill-neutral-100" />
                <p className="tw-text-neutral-100 tw-text-base tw-leading-5">Direct leader</p>
              </div>
              <DirectLeaderRender staffMember={jobInfoLeader} />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center">
                <SchemaOutlinedIcon className="tw-mr-2 !tw-w-5 !tw-h-5 tw-fill-neutral-100 -tw-rotate-90" />
                <p className="tw-text-neutral-100 tw-text-base tw-leading-5">
                  {jobInfo.directReports.length} Direct report
                  {jobInfo.directReports.length !== 1 ? 's' : ''}
                </p>
              </div>
              {jobInfo.directReports.map((directReport) => {
                if (!directReport.staffMember?.person) return null;
                return (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-pl-6" key={directReport.id}>
                    <UserInitials
                      className="!tw-w-6 !tw-h-6 !tw-mr-0"
                      firstName={directReport.staffMember.person.firstName}
                      lastName={directReport.staffMember.person.lastName}
                      pictureUrl={directReport.staffMember.person.pictureUrl || undefined}
                    />
                    <p className="tw-text-primary-60 tw-leading-normal tw-text-sm tw-font-semibold">
                      {directReport.staffMember.person.firstName}{' '}
                      {directReport.staffMember.person.lastName}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="tw-grid tw-grid-cols-2 tw-gap-x-10 tw-pb-2 tw-border-b tw-border-neutral-30">
            <DropDown
              isLabelAboveField
              label="Job Title"
              options={roleNames}
              name="title"
              defaultValue={values.title}
              onChangeDropDown={handleChange}
              errorMessage={(touched.title && errors.title) || undefined}
            />
            <DropDown
              isLabelAboveField
              label="Area"
              options={teamAreaOptions}
              name="area"
              defaultValue={values.area}
              errorMessage={(touched.area && errors.area) || undefined}
              onChangeDropDown={handleChange}
            />
            <DropDown
              isLabelAboveField
              label="Seniority"
              options={seniorityOptions}
              name="seniority"
              defaultValue={values.seniority}
              errorMessage={(touched.seniority && errors.seniority) || undefined}
              onChangeDropDown={handleChange}
            />
            <DropDown
              isLabelAboveField
              label="Team/Partner"
              optionsWithLabel={teamsList.map<ValuesWithLabel>((team) => ({
                value: team.id,
                label: team.name,
              }))}
              name="teamId"
              defaultValue={values.teamId}
              errorMessage={(touched.teamId && errors.teamId) || undefined}
              onChangeDropDown={handleChange}
            />
          </div>
          <div className="tw-py-6 tw-border-b tw-border-neutral-30 tw-grid tw-grid-cols-2 tw-gap-x-10">
            <AssignedUsers
              label="Direct leader"
              allReviewers={leadersFound.map((staffMember) => staffMember.person)}
              onAssign={handleSelectLeader}
              assignedUsers={leader ? [leader.person] : []}
              onRemoveAssignedUser={() => setLeader(undefined)}
              value={leaderSearch}
              onSearch={onLeaderSearch}
              placeholder="Search for a team member"
            />
            <AssignedUsers
              label="Direct reports"
              allReviewers={reportersFound.map((staffMember) => staffMember.person)}
              onAssign={handleSelectReporter}
              assignedUsers={reporters.map((staffMember) => staffMember.person)}
              onRemoveAssignedUser={handleRemoveReporter}
              value={reportersSearch}
              onSearch={onReportersSearch}
              placeholder="Search for a team member"
              disableRemoveButton={isRemovingReporter}
            />
          </div>
          <div className="tw-py-6 tw-border-b tw-border-neutral-30 tw-grid tw-grid-cols-2 tw-gap-x-2">
            <div>
              <DatePicker
                isLabelAboveField
                value={values.fromDate}
                name="from"
                label="from"
                onChange={(date) => setFieldValue('fromDate', date)}
                errorMessage={(touched.fromDate && errors.fromDate) || undefined}
                className="tw-mb-4"
              />
            </div>
            <div>
              <DatePicker
                isLabelAboveField
                value={values.toDate}
                name="to"
                label="to"
                minDate={values.fromDate ? new Date(values.fromDate) : null}
                onChange={(date) => setFieldValue('toDate', date)}
                errorMessage={(touched.toDate && errors.toDate) || undefined}
                className="tw-mb-4"
              />
            </div>
            <Checkbox
              label="Current job"
              name="isCurrentJob"
              checked={values.isCurrentJob}
              onChange={(e) => setFieldValue('isCurrentJob', e.target.checked)}
            />
          </div>
          <div className="tw-pt-6 tw-flex tw-gap-4">
            <TransparentButton isBlueButton icon={SaveIcon} type="submit" disabled={isLoading}>
              Save
            </TransparentButton>
            <TransparentButton
              type="button"
              icon={CancelIcon}
              isRedButton
              onClick={() => {
                if (jobInfo) {
                  onEditCancel?.();
                } else {
                  onAddCancel?.();
                }
                // form.resetForm();
              }}
              disabled={isLoading}
            >
              Cancel
            </TransparentButton>
          </div>
        </form>
      )}
    </div>
  );
};

export default StaffMemberJobItem;
